import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { encryptPassword } from '../../api/rsaEncrypt';
import { useToast } from '../../context/ToastContext';

const AI_PROVIDERS = [
  { key: 'OPENAI', name: 'OpenAI (ChatGPT)', placeholder: 'sk-...', hint: 'platform.openai.com/api-keys에서 발급' },
  { key: 'ANTHROPIC', name: 'Anthropic (Claude)', placeholder: 'sk-ant-...', hint: 'console.anthropic.com/settings/keys에서 발급' },
  { key: 'GEMINI', name: 'Google Gemini', placeholder: 'AIza...', hint: 'aistudio.google.com/app/apikey에서 발급' },
];

export default function Settings() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── API 키 관리 상태 ──────────────────────────────────────
  const [apiKeys, setApiKeys]           = useState([]);
  const [newKeyName, setNewKeyName]     = useState('');
  const [newKeyResult, setNewKeyResult] = useState(null); // 발급 직후 표시
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const connected = searchParams.get('connected');
  const oauthError = searchParams.get('error');

  const [integrations, setIntegrations] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [testResult, setTestResult] = useState({});
  const [loading, setLoading] = useState(false);

  const [notionToken, setNotionToken] = useState('');
  const [notionDbId, setNotionDbId] = useState('');
  const [notionSaving, setNotionSaving] = useState(false);

  const [aiKeys, setAiKeys] = useState({ OPENAI: '', ANTHROPIC: '', GEMINI: '' });
  const [aiSaving, setAiSaving] = useState('');

  const [adPublisherId, setAdPublisherId] = useState('');
  const [adHeaderSlotId, setAdHeaderSlotId] = useState('');
  const [adSidebarSlotId, setAdSidebarSlotId] = useState('');
  const [adFooterSlotId, setAdFooterSlotId] = useState('');
  const [adContentSlotId, setAdContentSlotId] = useState('');
  const [adSenseSaving, setAdSenseSaving] = useState(false);

  // 슬롯별 표시 여부
  const [adSlotVisible, setAdSlotVisible] = useState({
    header: true, sidebar: true, footer: true, content: true,
  });
  const [adSlotVisibilitySaving, setAdSlotVisibilitySaving] = useState(false);

  // 비밀번호 변경 상태
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  const bloggerAccount = integrations.find((i) => i.provider === 'BLOGGER');
  const notionAccount = integrations.find((i) => i.provider === 'NOTION');
  const adSenseAccount = integrations.find((i) => i.provider === 'ADSENSE');
  const getAiAccount = (key) => integrations.find((i) => i.provider === key);

  const loadApiKeys = () => {
    api.get('/api/admin/api-keys').then((res) => setApiKeys(res.data)).catch(() => {});
  };

  const handleGenerateApiKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setApiKeyLoading(true);
    try {
      const res = await api.post('/api/admin/api-keys', { name: newKeyName.trim() });
      setNewKeyResult(res.data);
      setNewKeyName('');
      loadApiKeys();
      toast('API 키가 발급되었습니다. 키 값을 안전한 곳에 저장하세요.', 'success', 6000);
    } catch (err) {
      toast('API 키 발급 실패: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRevokeApiKey = async (key) => {
    if (!window.confirm(`"${key.name}" 키를 비활성화하시겠습니까?`)) return;
    try {
      await api.patch(`/api/admin/api-keys/${key.id}/revoke`);
      toast('API 키가 비활성화되었습니다.', 'info');
      loadApiKeys();
    } catch (err) {
      toast('비활성화 실패', 'error');
    }
  };

  const handleDeleteApiKey = async (key) => {
    if (!window.confirm(`"${key.name}" 키를 완전히 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/api/admin/api-keys/${key.id}`);
      toast('API 키가 삭제되었습니다.', 'info');
      if (newKeyResult?.id === key.id) setNewKeyResult(null);
      loadApiKeys();
    } catch (err) {
      toast('삭제 실패', 'error');
    }
  };

  const load = () => {
    api.get('/api/admin/integrations').then((res) => setIntegrations(res.data)).catch(() => {});
    // 슬롯 표시 여부는 공개 AdSense 설정 API에서 읽음 (암호화 전 데이터)
    api.get('/api/public/settings/adsense')
      .then((res) => {
        if (res.data?.enabled !== false) {
          setAdSlotVisible({
            header: res.data?.headerVisible !== false,
            sidebar: res.data?.sidebarVisible !== false,
            footer: res.data?.footerVisible !== false,
            content: res.data?.contentVisible !== false,
          });
        }
      })
      .catch(() => {});
  };

  const fetchBlogList = () => {
    api.get('/api/admin/integrations/blogger/blogs')
      .then((res) => setBlogs(res.data))
      .catch(() => toast('블로그 목록 조회 실패.', 'error'));
  };

  useEffect(() => {
    load();
    loadApiKeys();
    if (connected === 'blogger') {
      toast('Google 계정이 연결되었습니다. 블로그를 선택하여 활성화해 주세요.', 'success', 5000);
      fetchBlogList();
    }
    if (oauthError) toast('Google 연결 실패: ' + oauthError, 'error', 5000);
  }, [connected, oauthError]);

  // Blogger 계정은 연결됐지만 블로그가 선택되지 않은 경우 자동으로 블로그 목록 로드
  useEffect(() => {
    if (bloggerAccount && !bloggerAccount.displayName?.includes(':') && blogs.length === 0) {
      fetchBlogList();
    }
  }, [bloggerAccount]);

  const handleBloggerConnect = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/integrations/blogger/auth-url');
      window.location.href = res.data.url;
    } catch { toast('OAuth URL 생성 실패.', 'error'); setLoading(false); }
  };

  const handleSelectBlog = async (blog) => {
    if (!bloggerAccount) return;
    try {
      await api.put(`/api/admin/integrations/${bloggerAccount.id}/select-blog`, {
        blogId: blog.id, blogUrl: blog.url, blogName: blog.name,
      });
      setBlogs([]);
      toast(`블로그 "${blog.name}" 선택 완료!`, 'success');
      load();
    } catch (err) { toast('블로그 선택 실패: ' + (err.response?.data?.message || err.message), 'error'); }
  };

  const handleNotionSave = async (e) => {
    e.preventDefault();
    setNotionSaving(true);
    try {
      await api.post('/api/admin/integrations/notion/save', { apiToken: notionToken, databaseId: notionDbId });
      toast('Notion 설정이 저장되었습니다.', 'success');
      setNotionToken(''); setNotionDbId('');
      load();
    } catch (err) { toast('Notion 저장 실패: ' + (err.response?.data?.message || err.message), 'error'); }
    finally { setNotionSaving(false); }
  };

  const handleAiSave = async (providerKey) => {
    const apiKey = aiKeys[providerKey];
    if (!apiKey.trim()) return;
    setAiSaving(providerKey);
    try {
      await api.post('/api/admin/integrations/ai/save', { provider: providerKey, apiKey });
      toast(`${providerKey} API 키가 저장되었습니다.`, 'success');
      setAiKeys((prev) => ({ ...prev, [providerKey]: '' }));
      load();
    } catch (err) { toast(`${providerKey} 저장 실패: ` + (err.response?.data?.message || err.message), 'error'); }
    finally { setAiSaving(''); }
  };

  const handleToggleEnable = async (account) => {
    try {
      await api.put(`/api/admin/integrations/${account.id}/enable`, { enabled: !account.enabled });
      toast(account.enabled ? '비활성화되었습니다.' : '활성화되었습니다.', 'info');
      load();
    }
    catch { toast('상태 변경 실패', 'error'); }
  };

  const handleTest = async (account) => {
    setTestResult((prev) => ({ ...prev, [account.id]: null }));
    try {
      const res = await api.post(`/api/admin/integrations/${account.id}/test`);
      setTestResult((prev) => ({ ...prev, [account.id]: res.data }));
    } catch { setTestResult((prev) => ({ ...prev, [account.id]: { success: false, message: '테스트 실패' } })); }
  };

  const handleAdSenseSave = async (e) => {
    e.preventDefault();
    if (!adPublisherId.trim()) return;
    setAdSenseSaving(true);
    try {
      await api.post('/api/admin/integrations/adsense/save', {
        publisherId: adPublisherId,
        headerSlotId: adHeaderSlotId,
        sidebarSlotId: adSidebarSlotId,
        footerSlotId: adFooterSlotId,
        contentSlotId: adContentSlotId,
      });
      toast('AdSense 설정이 저장되었습니다.', 'success');
      setAdPublisherId(''); setAdHeaderSlotId(''); setAdSidebarSlotId('');
      setAdFooterSlotId(''); setAdContentSlotId('');
      load();
    } catch (err) { toast('AdSense 저장 실패: ' + (err.response?.data?.message || err.message), 'error'); }
    finally { setAdSenseSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');

    // 프론트 유효성 검사
    if (pwNew !== pwConfirm) {
      setPwError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (pwNew.length < 8) {
      setPwError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }
    const typeCount = [/[a-zA-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((r) => r.test(pwNew)).length;
    if (typeCount < 2) {
      setPwError('비밀번호는 영문, 숫자, 특수문자 중 2종 이상을 포함해야 합니다.');
      return;
    }

    setPwSaving(true);
    try {
      // RSA 암호화 후 전송
      const [encCurrent, encNew, encConfirm] = await Promise.all([
        encryptPassword(pwCurrent),
        encryptPassword(pwNew),
        encryptPassword(pwConfirm),
      ]);
      await api.put('/api/admin/me/password', {
        currentPassword: encCurrent,
        newPassword: encNew,
        confirmPassword: encConfirm,
      });
      toast('비밀번호가 변경되었습니다. 다시 로그인해 주세요.', 'success', 3000);
      setTimeout(() => navigate('/admin/login', { replace: true }), 3000);
    } catch (err) {
      setPwError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setPwSaving(false);
    }
  };

  const handleAdSlotVisibilityToggle = (slot) => {
    setAdSlotVisible((prev) => ({ ...prev, [slot]: !prev[slot] }));
  };

  const handleAdSlotVisibilitySave = async () => {
    setAdSlotVisibilitySaving(true);
    try {
      await api.put('/api/admin/integrations/adsense/slot-visibility', {
        headerVisible: adSlotVisible.header,
        sidebarVisible: adSlotVisible.sidebar,
        footerVisible: adSlotVisible.footer,
        contentVisible: adSlotVisible.content,
      });
      toast('광고 슬롯 표시 설정이 저장되었습니다.', 'success');
      load();
    } catch (err) {
      toast('저장 실패: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setAdSlotVisibilitySaving(false);
    }
  };

  const handleDisconnect = async (account) => {
    if (!window.confirm('연결을 해제하시겠습니까?')) return;
    try { await api.delete(`/api/admin/integrations/${account.id}`); toast('연결 해제되었습니다.', 'info'); load(); }
    catch { toast('연결 해제 실패', 'error'); }
  };

  const IntegrationControls = ({ account }) => (
    <div className="integration-actions">
      <button onClick={() => handleToggleEnable(account)} className="btn-sm">
        {account.enabled ? '비활성화' : '활성화'}
      </button>
      <button onClick={() => handleTest(account)} className="btn-sm">연결 테스트</button>
      <button onClick={() => handleDisconnect(account)} className="btn-sm btn-danger">해제</button>
      {testResult[account.id] && (
        <span className={testResult[account.id].success ? 'success-msg' : 'error-msg'}>
          {testResult[account.id].message}
        </span>
      )}
    </div>
  );

  return (
    <div className="settings-page">
      <h2>설정</h2>

      {/* Blogger */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Blogger 연동</h3>
          <span className={`status-chip ${bloggerAccount?.enabled ? 'chip-active' : bloggerAccount ? 'chip-inactive' : 'disconnected'}`}>
            {bloggerAccount?.enabled ? '활성' : bloggerAccount ? '비활성' : '미연결'}
          </span>
        </div>
        <div className="settings-card-body">
          <p className="integration-info">글 발행 시 Blogger에 자동으로 동기화됩니다.</p>
          {!bloggerAccount ? (
            <button onClick={handleBloggerConnect} disabled={loading} className="btn-primary">
              {loading ? '연결 중...' : 'Google 계정 연결'}
            </button>
          ) : (
            <>
              <p style={{ margin: '0 0 8px' }}><strong>연결된 계정:</strong> {bloggerAccount.displayName}</p>
              <IntegrationControls account={bloggerAccount} />
              {bloggerAccount.displayName?.includes(':') && blogs.length === 0 && (
                <button onClick={fetchBlogList} className="btn-sm" style={{ marginTop: '8px' }}>
                  블로그 변경
                </button>
              )}
            </>
          )}
          {blogs.length > 0 && (
            <div className="blog-select">
              <h4>발행할 블로그를 선택하세요</h4>
              <ul className="blog-list">
                {blogs.map((blog) => (
                  <li key={blog.id} className="blog-item">
                    <div><strong>{blog.name}</strong><br /><a href={blog.url} target="_blank" rel="noopener noreferrer">{blog.url}</a></div>
                    <button onClick={() => handleSelectBlog(blog)} className="btn-primary btn-sm-pad">선택</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Notion */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Notion 연동</h3>
          <span className={`status-chip ${notionAccount?.enabled ? 'chip-active' : notionAccount ? 'chip-inactive' : 'disconnected'}`}>
            {notionAccount?.enabled ? '활성' : notionAccount ? '비활성' : '미연결'}
          </span>
        </div>
        <div className="settings-card-body">
          <p className="integration-info">글 발행 시 Notion Database에 자동으로 페이지를 생성합니다.</p>
          <form onSubmit={handleNotionSave} className="notion-form">
            <div className="form-group">
              <label>Integration Token</label>
              <input type="password" className="form-input" placeholder="secret_xxxxxxxxxxxxxxxxxxxx"
                value={notionToken} onChange={(e) => setNotionToken(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Database ID</label>
              <input type="text" className="form-input" placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={notionDbId} onChange={(e) => setNotionDbId(e.target.value)} required />
            </div>
            <button type="submit" disabled={notionSaving} className="btn-primary">
              {notionSaving ? '저장 중...' : notionAccount ? '업데이트' : '연동 저장'}
            </button>
          </form>
          {notionAccount && <IntegrationControls account={notionAccount} />}
        </div>
      </div>

      {/* AI Providers */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>AI 글 생성 연동</h3>
        </div>
        <div className="settings-card-body">
          <p className="integration-info">
            API 키를 저장하고 활성화하면 글 편집 페이지에서 AI 초안 자동 생성 기능을 사용할 수 있습니다.
            결과는 항상 임시저장(DRAFT) 상태로 저장됩니다.
          </p>
          {AI_PROVIDERS.map(({ key, name, placeholder, hint }) => {
            const account = getAiAccount(key);
            return (
              <div key={key} className="ai-provider-row">
                <div className="ai-provider-header">
                  <span className="ai-provider-name">{name}</span>
                  <span className={`status-chip ${account?.enabled ? 'chip-active' : account ? 'chip-inactive' : 'disconnected'}`}>
                    {account?.enabled ? '활성' : account ? '비활성' : '미설정'}
                  </span>
                </div>
                <div className="ai-key-form">
                  <input
                    type="password"
                    className="form-input ai-key-input"
                    placeholder={placeholder}
                    value={aiKeys[key]}
                    onChange={(e) => setAiKeys((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                  <button
                    onClick={() => handleAiSave(key)}
                    disabled={aiSaving === key || !aiKeys[key].trim()}
                    className="btn-secondary"
                  >
                    {aiSaving === key ? '저장 중...' : account ? '업데이트' : '저장'}
                  </button>
                </div>
                <small className="form-hint">{hint}</small>
                {account && <IntegrationControls account={account} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* AdSense */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Google AdSense</h3>
          <span className={`status-chip ${adSenseAccount?.enabled ? 'chip-active' : adSenseAccount ? 'chip-inactive' : 'disconnected'}`}>
            {adSenseAccount?.enabled ? '활성' : adSenseAccount ? '비활성' : '미설정'}
          </span>
        </div>
        <div className="settings-card-body">
          <p className="integration-info">
            Publisher ID와 각 위치별 광고 슬롯 ID를 입력하면 블로그 헤더·사이드바·푸터·본문에 AdSense 광고가 표시됩니다.
            저장 후 <strong>활성화</strong>하면 실제 광고 코드가 삽입됩니다.
          </p>
          <form onSubmit={handleAdSenseSave} className="adsense-form">
            <div className="form-group">
              <label>Publisher ID <span className="required">*</span></label>
              <input type="text" className="form-input" placeholder="ca-pub-XXXXXXXXXXXXXXXXXX"
                value={adPublisherId} onChange={(e) => setAdPublisherId(e.target.value)} required />
              <small className="form-hint">Google AdSense 계정의 게시자 ID (ca-pub-으로 시작)</small>
            </div>
            <div className="adsense-slots">
              <div className="form-group">
                <label>헤더 슬롯 ID</label>
                <input type="text" className="form-input" placeholder="XXXXXXXXXX"
                  value={adHeaderSlotId} onChange={(e) => setAdHeaderSlotId(e.target.value)} />
              </div>
              <div className="form-group">
                <label>사이드바 슬롯 ID</label>
                <input type="text" className="form-input" placeholder="XXXXXXXXXX"
                  value={adSidebarSlotId} onChange={(e) => setAdSidebarSlotId(e.target.value)} />
              </div>
              <div className="form-group">
                <label>푸터 슬롯 ID</label>
                <input type="text" className="form-input" placeholder="XXXXXXXXXX"
                  value={adFooterSlotId} onChange={(e) => setAdFooterSlotId(e.target.value)} />
              </div>
              <div className="form-group">
                <label>본문 슬롯 ID</label>
                <input type="text" className="form-input" placeholder="XXXXXXXXXX"
                  value={adContentSlotId} onChange={(e) => setAdContentSlotId(e.target.value)} />
              </div>
            </div>
            <button type="submit" disabled={adSenseSaving || !adPublisherId.trim()} className="btn-primary">
              {adSenseSaving ? '저장 중...' : adSenseAccount ? '업데이트' : '저장'}
            </button>
          </form>

          {/* 슬롯별 광고 표시/숨김 설정 */}
          {adSenseAccount && (
            <div className="adsense-visibility">
              <h4 className="adsense-visibility-title">슬롯별 광고 표시 설정</h4>
              <p className="integration-info" style={{ margin: '0 0 12px' }}>
                각 위치의 광고를 개별적으로 켜고 끌 수 있습니다. 슬롯 ID가 없는 위치는 설정과 무관하게 표시되지 않습니다.
              </p>
              <div className="adsense-visibility-grid">
                {[
                  { key: 'header', label: '헤더' },
                  { key: 'sidebar', label: '사이드바' },
                  { key: 'footer', label: '푸터' },
                  { key: 'content', label: '본문' },
                ].map(({ key, label }) => (
                  <label key={key} className="adsense-slot-toggle">
                    <input
                      type="checkbox"
                      checked={adSlotVisible[key]}
                      onChange={() => handleAdSlotVisibilityToggle(key)}
                    />
                    <span className="slot-toggle-label">
                      {label} 광고 {adSlotVisible[key] ? '표시' : '숨김'}
                    </span>
                    <span className={`status-chip ${adSlotVisible[key] ? 'chip-active' : 'chip-inactive'}`} style={{ marginLeft: 'auto' }}>
                      {adSlotVisible[key] ? 'ON' : 'OFF'}
                    </span>
                  </label>
                ))}
              </div>
              <button
                onClick={handleAdSlotVisibilitySave}
                disabled={adSlotVisibilitySaving}
                className="btn-secondary"
                style={{ marginTop: '12px' }}
              >
                {adSlotVisibilitySaving ? '저장 중...' : '표시 설정 저장'}
              </button>
            </div>
          )}

          {adSenseAccount && <IntegrationControls account={adSenseAccount} />}
        </div>
      </div>

      {/* 계정 설정 — 비밀번호 변경 */}
      <div className="settings-card">
        <div className="settings-card-header"><h3>계정 설정</h3></div>
        <div className="settings-card-body">
          <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#374151' }}>비밀번호 변경</h4>
          <form onSubmit={handlePasswordChange} className="pw-change-form">
            <div className="form-group">
              <label>현재 비밀번호</label>
              <input type="password" className="form-input" value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)} required autoComplete="current-password" />
            </div>
            <div className="form-group">
              <label>새 비밀번호</label>
              <input type="password" className="form-input" value={pwNew}
                onChange={(e) => setPwNew(e.target.value)} required autoComplete="new-password" />
              <small className="form-hint">최소 8자, 영문/숫자/특수문자 중 2종 이상</small>
            </div>
            <div className="form-group">
              <label>새 비밀번호 확인</label>
              <input type="password" className="form-input" value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)} required autoComplete="new-password" />
            </div>
            {pwError && <p className="error-msg">{pwError}</p>}
            <button type="submit" disabled={pwSaving} className="btn-primary">
              {pwSaving ? '변경 중...' : '비밀번호 변경'}
            </button>
          </form>
        </div>
      </div>

      {/* 외부 API 키 관리 */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>외부 API 키 관리</h3>
        </div>
        <div className="settings-card-body">
          <p className="integration-info">
            API 키를 발급하면 다른 AI 도구(Claude MCP, ChatGPT Actions 등)에서
            블로그에 글을 직접 생성·발행할 수 있습니다.
            키 값은 발급 직후에만 표시되므로 안전한 곳에 저장하세요.
          </p>
          <p className="integration-info">
            <strong>인증 방법:</strong> HTTP 헤더에 <code>X-Api-Key: &lt;키 값&gt;</code> 추가
            &nbsp;|&nbsp;
            <strong>엔드포인트:</strong> <code>POST {window.location.origin}/api/v1/posts</code>
          </p>

          {/* 새 키 발급 폼 */}
          <form onSubmit={handleGenerateApiKey} className="api-key-form">
            <input
              type="text"
              className="form-input"
              placeholder="키 이름 (예: Claude MCP, My Bot)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              required
            />
            <button type="submit" disabled={apiKeyLoading || !newKeyName.trim()} className="btn-primary">
              {apiKeyLoading ? '발급 중...' : '새 키 발급'}
            </button>
          </form>

          {/* 발급 직후 키 값 표시 */}
          {newKeyResult && (
            <div className="api-key-new-result">
              <p className="api-key-warn">⚠ 키 값은 지금 이 화면에서만 볼 수 있습니다. 복사하여 안전하게 보관하세요.</p>
              <div className="api-key-value-box">
                <code>{newKeyResult.keyValue}</code>
                <button
                  className="btn-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(newKeyResult.keyValue);
                    toast('클립보드에 복사되었습니다.', 'success');
                  }}
                >
                  복사
                </button>
              </div>
            </div>
          )}

          {/* 기존 키 목록 */}
          {apiKeys.length > 0 && (
            <table className="api-key-table">
              <thead>
                <tr>
                  <th>이름</th>
                  <th>상태</th>
                  <th>발급일</th>
                  <th>마지막 사용</th>
                  <th>액션</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td>
                      <span className={`status-chip ${k.active ? 'chip-active' : 'chip-inactive'}`}>
                        {k.active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      {k.createdAt ? new Date(k.createdAt).toLocaleDateString('ko-KR') : '-'}
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString('ko-KR') : '미사용'}
                    </td>
                    <td>
                      <div className="integration-actions">
                        {k.active && (
                          <button onClick={() => handleRevokeApiKey(k)} className="btn-sm">비활성화</button>
                        )}
                        <button onClick={() => handleDeleteApiKey(k)} className="btn-sm btn-danger">삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {apiKeys.length === 0 && (
            <p style={{ color: '#9ca3af', fontSize: '0.82rem', marginTop: '12px' }}>
              발급된 API 키가 없습니다.
            </p>
          )}
        </div>
      </div>

      {/* MCP 서버 설정 안내 */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>MCP 서버 연결 가이드</h3>
          <span className="status-chip chip-active">활성</span>
        </div>
        <div className="settings-card-body">
          <p className="integration-info">
            이 블로그는 <strong>MCP (Model Context Protocol)</strong> 서버를 내장하고 있습니다.
            Claude Desktop, Cursor, Cline 등 MCP를 지원하는 AI 도구에서 블로그에 직접 글을 작성·발행할 수 있습니다.
          </p>

          <div className="mcp-info-block">
            <h4>서버 엔드포인트</h4>
            <div className="api-key-value-box">
              <code>{window.location.origin}/mcp</code>
              <button
                className="btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/mcp`);
                  toast('클립보드에 복사되었습니다.', 'success');
                }}
              >
                복사
              </button>
            </div>
          </div>

          <div className="mcp-info-block">
            <h4>Claude Desktop 설정 예시</h4>
            <p className="integration-info" style={{ marginBottom: '6px' }}>
              <code>~/Library/Application Support/Claude/claude_desktop_config.json</code> 파일에 아래 내용을 추가하세요.
            </p>
            <pre className="mcp-code-block">{`{
  "mcpServers": {
    "hobbygom-blog": {
      "command": "npx",
      "args": [
        "mcp-remote",
        "${window.location.origin}/mcp",
        "--header",
        "X-Api-Key: hbg_여기에_발급받은_키_입력"
      ]
    }
  }
}`}</pre>
          </div>

          <div className="mcp-info-block">
            <h4>curl 테스트</h4>
            <pre className="mcp-code-block">{`# 도구 목록 조회
curl -X POST ${window.location.origin}/mcp \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: hbg_여기에_키_입력" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'

# 글 생성 예시
curl -X POST ${window.location.origin}/mcp \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: hbg_여기에_키_입력" \\
  -d '{"jsonrpc":"2.0","id":2,"method":"tools/call","params":{"name":"create_post","arguments":{"title":"AI가 작성한 글","contentHtml":"<p>내용</p>","publish":false}}}'`}</pre>
          </div>

          <div className="mcp-info-block">
            <h4>지원 도구 목록</h4>
            <table className="api-key-table">
              <thead>
                <tr><th>도구명</th><th>설명</th></tr>
              </thead>
              <tbody>
                {[
                  ['list_categories', '카테고리 목록 조회'],
                  ['list_posts',      '글 목록 조회 (페이징·상태 필터)'],
                  ['get_post',        'slug로 글 조회'],
                  ['create_post',     '글 생성 (publish=true 이면 즉시 발행)'],
                  ['update_post',     '기존 글 수정'],
                  ['upload_image',    '이미지 업로드 (base64 → URL 반환)'],
                  ['publish_post',    'DRAFT 글 발행'],
                ].map(([name, desc]) => (
                  <tr key={name}>
                    <td><code>{name}</code></td>
                    <td style={{ fontSize: '0.82rem', color: '#374151' }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="integration-info" style={{ marginTop: '12px' }}>
            ※ API 키는 위의 <strong>외부 API 키 관리</strong> 섹션에서 발급받아 사용하세요.
          </p>
        </div>
      </div>

      {/* 보안 안내 */}
      <div className="settings-card">
        <div className="settings-card-header"><h3>보안 설정 안내</h3></div>
        <div className="settings-card-body">
          <ul className="settings-info-list">
            <li>모든 자격증명(API Key, Token)은 AES-256으로 암호화되어 DB에 저장됩니다.</li>
            <li>암호화 키: 환경변수 <code>ENCRYPTION_KEY</code> (32자)</li>
            <li>Google OAuth: 환경변수 <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code></li>
            <li>SMTP 메일: 환경변수 <code>MAIL_HOST</code>, <code>MAIL_USERNAME</code>, <code>MAIL_PASSWORD</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
